import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, Lock, Unlock, GripVertical, FileText, AlertCircle,
  ZoomIn, ZoomOut, RotateCcw, X, Grid3x3, Trash2, Plus, Upload, Image as ImageIcon
} from 'lucide-react';
import { PdfPageBackground } from '../components/PdfOverlay';
import BookingDetailModal from '../components/BookingDetailModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const isImageFile = (filename) => {
  if (!filename) return false;
  return ['jpg', 'jpeg', 'png'].includes(filename.toLowerCase().split('.').pop());
};

// Calculate ad size as percentage of page using real dimensions (margins excluded from denominator)
const getAdSizePercent = (booking, productTypes, pageDims) => {
  const pageW = pageDims?.page_width || 11;
  const pageH = pageDims?.page_height || 17;
  const mt = pageDims?.margin_top || 0;
  const mb = pageDims?.margin_bottom || 0;
  const mi = pageDims?.margin_inside || 0;
  const mo = pageDims?.margin_outside || 0;
  const usableW = Math.max(pageW - mi - mo, 0.0001);
  const usableH = Math.max(pageH - mt - mb, 0.0001);
  const usableArea = usableW * usableH;
  const pt = productTypes.find(p => p.name === booking.product_type);
  const adW = pt?.width ?? booking.width;
  const adH = pt?.height ?? booking.height;

  if (adW > 0 && adH > 0) {
    // Clamp ad area to one full usable page so a "Full Page" ad
    // (often configured at trim size) counts as exactly 100%
    const adArea = Math.min(adW * adH, usableArea);
    return {
      widthPct: Math.min((adW / pageW) * 100, 100),
      heightPct: Math.min((adH / pageH) * 100, 100),
      fillPct: Math.min((adArea / usableArea) * 100, 100),
      label: `${adW}" x ${adH}"`
    };
  }
  const name = booking.product_type?.toLowerCase() || '';
  if (name.includes('full'))    return { widthPct: 100, heightPct: 100, fillPct: 100, label: 'Full' };
  if (name.includes('half'))    return { widthPct: 100, heightPct: 50,  fillPct: 50,  label: '1/2' };
  if (name.includes('quarter')) return { widthPct: 50,  heightPct: 50,  fillPct: 25,  label: '1/4' };
  if (name.includes('eighth'))  return { widthPct: 50,  heightPct: 25,  fillPct: 12.5, label: '1/8' };
  return { widthPct: 50, heightPct: 50, fillPct: 25, label: '?' };
};

const FlatplanPage = () => {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'sales_rep';
  const { editionId } = useParams();
  const navigate = useNavigate();
  const [edition, setEdition] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [pageDimensions, setPageDimensions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);

  // DnD from sidebar to page
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dragOverPage, setDragOverPage] = useState(null);

  // In-page repositioning + inter-page transfer
  const dragRef = useRef(null);
  const [localOffsets, setLocalOffsets] = useState({});
  const pageRefsMap = useRef({});
  const transferRef = useRef(null);
  const [transferTargetPage, setTransferTargetPage] = useState(null);

  // Snap to grid
  const [snapEnabled, setSnapEnabled] = useState(false);
  const GRID_COLS = 4;
  const GRID_ROWS = 8;
  const SNAP_X = 100 / GRID_COLS;  // 25%
  const SNAP_Y = 100 / GRID_ROWS;  // 12.5%

  // PDF Overlay (server-side pre-rendered images)
  const [overlayImages, setOverlayImages] = useState({}); // { pageNum: fullUrl }
  const [overlayOpacity, setOverlayOpacity] = useState(15);
  const [overlayGrayscale, setOverlayGrayscale] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [processProgress, setProcessProgress] = useState({ progress: 0, total: 0 });
  const pdfInputRef = useRef(null);
  const hasOverlay = Object.keys(overlayImages).length > 0;

  const snap = (val, step) => Math.round(val / step) * step;

  // Ad detail modal (double-click)
  const [editingBooking, setEditingBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Zoom controls columns-per-row instead of CSS scale
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2;
  const ZOOM_STEP = 0.25;

  const getGridCols = (zoom) => {
    if (zoom <= 0.5) return 8;
    if (zoom <= 0.75) return 7;
    if (zoom <= 1) return 6;
    if (zoom <= 1.25) return 5;
    if (zoom <= 1.5) return 4;
    if (zoom <= 1.75) return 3;
    return 2;
  };

  useEffect(() => { if (editionId) loadData(); }, [editionId]);

  const loadData = async () => {
    try {
      const [ed, bk, pt, st] = await Promise.all([
        api.get(`/editions/${editionId}`),
        api.get(`/bookings?edition_id=${editionId}`),
        api.get('/product-types?category=digital_edition'),
        api.get('/settings')
      ]);
      setEdition(ed); setBookings(bk); setProductTypes(pt); setPageDimensions(st);
      // Load pre-rendered overlay images (skip null/empty entries — failed pages)
      const images = ed.overlay_page_images || [];
      if (images.length > 0) {
        const imgMap = {};
        images.forEach((url, i) => {
          if (url) imgMap[i + 1] = `${process.env.REACT_APP_BACKEND_URL}${url}`;
        });
        setOverlayImages(imgMap);
      }
    } catch (err) { console.error('Failed to load data:', err); }
    finally { setLoading(false); }
  };

  // Zoom
  const zoomIn = () => setZoomLevel(p => Math.min(p + ZOOM_STEP, ZOOM_MAX));
  const zoomOut = () => setZoomLevel(p => Math.max(p - ZOOM_STEP, ZOOM_MIN));
  const resetZoom = () => setZoomLevel(1);

  // PDF Overlay upload + server-side processing
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Please select a PDF file.');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setProcessing(false);
    setProcessProgress({ progress: 0, total: 0 });

    try {
      // Upload + process via multipart form
      const formData = new FormData();
      formData.append('file', file);

      // Phase 1: Upload with progress
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/editions/${editionId}/process-pdf`,
        formData,
        {
          withCredentials: true,
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(pct);
            if (pct >= 100) {
              setUploading(false);
              setProcessing(true);
            }
          }
        }
      );

      // Phase 2: Done — set images (skip null entries for failed pages)
      const images = response.data.images || [];
      const failedPages = response.data.failed_pages || [];
      const imgMap = {};
      images.forEach((url, i) => {
        if (url) imgMap[i + 1] = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      });
      setOverlayImages(imgMap);
      setEdition(prev => ({ ...prev, overlay_page_images: images, pdf_overlay_url: null }));
      if (failedPages.length > 0) {
        const failedList = failedPages.map(f => `p.${f.page}`).join(', ');
        alert(`Warning: ${failedPages.length} of ${images.length} PDF page(s) could not be converted (${failedList}). Other pages loaded successfully. Check backend logs for details.`);
      }
    } catch (err) {
      console.error('PDF processing failed:', err);
      alert('Failed to process PDF.');
    } finally {
      setUploading(false);
      setProcessing(false);
      setUploadProgress(0);
      setProcessProgress({ progress: 0, total: 0 });
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  // Poll processing status while processing
  useEffect(() => {
    if (!processing) return;
    const interval = setInterval(async () => {
      try {
        const status = await api.get(`/editions/${editionId}/process-status`);
        if (status.status === 'processing') {
          setProcessProgress({ progress: status.progress, total: status.total });
        } else if (status.status === 'done') {
          setProcessing(false);
        }
      } catch {}
    }, 500);
    return () => clearInterval(interval);
  }, [processing, editionId]);

  const handleRemoveOverlay = async () => {
    try {
      await api.put(`/editions/${editionId}`, { overlay_page_images: [], pdf_overlay_url: '' });
      setOverlayImages({});
      setEdition(prev => ({ ...prev, overlay_page_images: [], pdf_overlay_url: null }));
    } catch (err) { console.error('Failed to remove overlay:', err); }
  };

  const handleAdDoubleClick = (booking) => {
    setEditingBooking(booking);
    setShowBookingModal(true);
  };

  // Derived lists
  const unplacedBookings = bookings.filter(
    b => (b.status === 'confirmed' || b.status === 'production' || b.status === 'paid' || b.status === 'invoiced') && !b.page_number && b.category === 'digital_edition'
  );
  const placedBookings = bookings.filter(b => b.page_number);
  const getSectionForPage = (n) => edition?.sections?.find(s => n >= s.start_page && n <= s.end_page);
  const getBookingsForPage = (n) => placedBookings.filter(b => b.page_number === n);

  const getPageFillPercentage = (n) =>
    getBookingsForPage(n).reduce((t, b) => t + getAdSizePercent(b, productTypes, pageDimensions).fillPct, 0);

  // ─── Sidebar → Page DnD (HTML5) ───
  const handleDragStart = (e, booking) => {
    if ((edition?.is_locked || isReadOnly)) { e.preventDefault(); return; }
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.id);
  };

  const handleDragOver = (e, pageNum) => {
    e.preventDefault();
    if ((edition?.is_locked || isReadOnly) || !draggedBooking) return;
    const fill = getPageFillPercentage(pageNum);
    const { heightPct } = getAdSizePercent(draggedBooking, productTypes, pageDimensions);
    const onSame = draggedBooking.page_number === pageNum;
    const adj = onSame ? fill - heightPct : fill;
    setDragOverPage({ pageNum, valid: adj + heightPct <= 100 });
  };

  const handleDrop = async (e, pageNum) => {
    e.preventDefault();
    if (!draggedBooking || (edition?.is_locked || isReadOnly)) return;
    const fill = getPageFillPercentage(pageNum);
    const { heightPct } = getAdSizePercent(draggedBooking, productTypes, pageDimensions);
    const onSame = draggedBooking.page_number === pageNum;
    const adj = onSame ? fill - heightPct : fill;
    if (adj + heightPct > 100) { setDraggedBooking(null); setDragOverPage(null); return; }
    try {
      await api.post(`/bookings/${draggedBooking.id}/place`, {
        booking_id: draggedBooking.id, page_number: pageNum,
        position: draggedBooking.product_type, x_offset: 0, y_offset: 0
      });
      await loadData();
    } catch (err) { console.error('Failed to place:', err); }
    setDraggedBooking(null); setDragOverPage(null);
  };

  const handleDragEnd = () => { setDraggedBooking(null); setDragOverPage(null); };

  // ─── In-Page Repositioning (mouse) ───
  const handleAdMouseDown = useCallback((e, booking, pageEl) => {
    if ((edition?.is_locked || isReadOnly)) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = pageEl.getBoundingClientRect();
    const sz = getAdSizePercent(booking, productTypes, pageDimensions);
    dragRef.current = {
      bookingId: booking.id,
      productType: booking.product_type,
      sourcePageNum: booking.page_number,
      pageRect: rect,
      startX: e.clientX,
      startY: e.clientY,
      origX: booking.x_offset || 0,
      origY: booking.y_offset || 0,
      wPct: sz.widthPct,
      hPct: sz.heightPct,
      snap: snapEnabled,
      snapX: SNAP_X,
      snapY: SNAP_Y
    };
    document.body.style.cursor = 'grabbing';
  }, [edition, productTypes, pageDimensions, snapEnabled, SNAP_X, SNAP_Y]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Event listeners use refs for mutable state (dragRef, transferRef). Only localOffsets triggers re-registration since it's read during mouseUp.
  useEffect(() => {
    const onMouseMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const { pageRect } = d;
      const inPage = e.clientX >= pageRect.left && e.clientX <= pageRect.right &&
                     e.clientY >= pageRect.top && e.clientY <= pageRect.bottom;

      if (inPage) {
        // Within page — reposition
        transferRef.current = null;
        setTransferTargetPage(null);
        const dx = ((e.clientX - d.startX) / pageRect.width) * 100;
        const dy = ((e.clientY - d.startY) / pageRect.height) * 100;
        const maxX = 100 - d.wPct;
        const maxY = 100 - d.hPct;
        let nx = Math.max(0, Math.min(d.origX + dx, maxX));
        let ny = Math.max(0, Math.min(d.origY + dy, maxY));
        if (d.snap) {
          nx = Math.max(0, Math.min(snap(nx, d.snapX), maxX));
          ny = Math.max(0, Math.min(snap(ny, d.snapY), maxY));
        }
        setLocalOffsets(prev => ({ ...prev, [d.bookingId]: { x: nx, y: ny } }));
      } else {
        // Outside page — find target page for inter-page transfer
        let target = null;
        for (const [pn, el] of Object.entries(pageRefsMap.current)) {
          if (!el) continue;
          const r = el.getBoundingClientRect();
          if (e.clientX >= r.left && e.clientX <= r.right &&
              e.clientY >= r.top && e.clientY <= r.bottom) {
            target = parseInt(pn);
            break;
          }
        }
        transferRef.current = target;
        setTransferTargetPage(target);
      }
    };
    const onMouseUp = async () => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      document.body.style.cursor = '';

      const targetPage = transferRef.current;
      transferRef.current = null;
      setTransferTargetPage(null);

      if (targetPage && targetPage !== d.sourcePageNum) {
        // Inter-page transfer
        try {
          await api.post(`/bookings/${d.bookingId}/place`, {
            booking_id: d.bookingId,
            page_number: targetPage,
            position: d.productType,
            x_offset: 0,
            y_offset: 0
          });
          await loadData();
        } catch (err) { console.error('Transfer failed:', err); }
      } else if (!targetPage) {
        // Within-page reposition
        const off = localOffsets[d.bookingId];
        if (off) {
          try {
            await api.put(`/bookings/${d.bookingId}/reposition`, { x_offset: off.x, y_offset: off.y });
            setBookings(prev => prev.map(b => b.id === d.bookingId ? { ...b, x_offset: off.x, y_offset: off.y } : b));
          } catch (err) { console.error('Reposition failed:', err); }
        }
      }
      setLocalOffsets(prev => { const copy = { ...prev }; delete copy[d.bookingId]; return copy; });
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, [localOffsets]);

  // ─── Unplace ───
  const handleUnplace = async (bookingId) => {
    if ((edition?.is_locked || isReadOnly)) return;
    try {
      await api.post(`/bookings/${bookingId}/unplace`);
      await loadData();
    } catch (err) { console.error('Failed to unplace:', err); }
  };

  const toggleLock = async () => {
    try {
      await api.put(`/editions/${editionId}`, { is_locked: !edition.is_locked });
      loadData();
    } catch (err) { console.error('Failed to toggle lock:', err); }
  };

  // ─── Add / Delete Pages ───
  const handleAddPage = async () => {
    if ((edition?.is_locked || isReadOnly)) return;
    try {
      await api.put(`/editions/${editionId}`, { page_count: edition.page_count + 1 });
      await loadData();
    } catch (err) { console.error('Failed to add page:', err); }
  };

  const handleDeletePage = async (pageNum) => {
    if ((edition?.is_locked || isReadOnly)) return;
    const adsOnPage = getBookingsForPage(pageNum);
    if (adsOnPage.length > 0) {
      if (!window.confirm(`Page ${pageNum} has ${adsOnPage.length} placed ad(s). They will be unplaced. Continue?`)) return;
      for (const b of adsOnPage) {
        await api.post(`/bookings/${b.id}/unplace`);
      }
    }
    // Shift bookings on pages after the deleted one
    const higher = placedBookings.filter(b => b.page_number > pageNum);
    for (const b of higher) {
      await api.post(`/bookings/${b.id}/place`, {
        booking_id: b.id, page_number: b.page_number - 1,
        position: b.position || b.product_type,
        x_offset: b.x_offset || 0, y_offset: b.y_offset || 0
      });
    }
    await api.put(`/editions/${editionId}`, { page_count: edition.page_count - 1 });
    await loadData();
  };

  const pages = edition ? Array.from({ length: edition.page_count }, (_, i) => i + 1) : [];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
    </div>
  );

  if (!edition) return (
    <div className="flex flex-col items-center justify-center h-64 text-[#94A3B8]">
      <AlertCircle className="w-16 h-16 mb-4 opacity-50" />
      <p className="text-lg">Edition not found</p>
      <Button onClick={() => navigate('/editions')} variant="outline" className="mt-4 border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]">Back to Editions</Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="flatplan-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/editions')} className="text-[#94A3B8] hover:text-[#F8FAFC]" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#F8FAFC] tracking-tight">{edition.name}</h1>
            <p className="text-[#94A3B8] text-sm">
              {edition.page_count} pages - Flatplan Editor
              {pageDimensions && <span className="ml-2 text-[#475569]">({pageDimensions.page_width}" &times; {pageDimensions.page_height}" page)</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Snap to Grid Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSnapEnabled(p => !p)}
            className={`border-[#1E293B] transition-colors ${
              snapEnabled ? 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/50' : 'text-[#94A3B8]'
            }`}
            data-testid="snap-toggle-btn"
            title={snapEnabled ? 'Disable snap to grid' : 'Enable snap to grid'}
          >
            <Grid3x3 className="w-4 h-4 mr-2" />
            Snap
          </Button>

          {/* PDF Overlay Controls */}
          {!isReadOnly && (
          <div className="flex items-center gap-2 border-l border-[#1E293B] pl-3">
            <input
              ref={pdfInputRef}
              type="file"
              accept=".pdf"
              onChange={handlePdfUpload}
              className="hidden"
              data-testid="pdf-upload-input"
            />
            {(uploading || processing) ? (
              <div className="flex items-center gap-2 min-w-[200px]">
                <div className="flex-1 space-y-1">
                  <p className="text-[10px] text-[#94A3B8]">
                    {uploading ? 'Uploading...' : `Processing page ${processProgress.progress}/${processProgress.total || '?'}...`}
                  </p>
                  <Progress
                    value={uploading ? uploadProgress : (processProgress.total > 0 ? (processProgress.progress / processProgress.total) * 100 : 0)}
                    className="h-2"
                    data-testid="pdf-progress-bar"
                  />
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => pdfInputRef.current?.click()}
                className="border-[#1E293B] text-[#94A3B8]"
                data-testid="upload-pdf-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {hasOverlay ? 'Replace PDF' : 'Upload PDF'}
              </Button>
            )}
            {hasOverlay && !uploading && !processing && (
              <>
                <div className="flex items-center gap-2 bg-[#121212] border border-[#1E293B] rounded-md px-3 py-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-[#94A3B8]" />
                  <Slider
                    value={[overlayOpacity]}
                    onValueChange={([v]) => setOverlayOpacity(v)}
                    min={0}
                    max={100}
                    step={5}
                    className="w-20"
                    data-testid="overlay-opacity-slider"
                  />
                  <span className="text-[10px] text-[#94A3B8] w-7 text-right">{overlayOpacity}%</span>
                </div>
                <div className="flex items-center gap-1.5 bg-[#121212] border border-[#1E293B] rounded-md px-2 py-1.5">
                  <Switch
                    checked={overlayGrayscale}
                    onCheckedChange={setOverlayGrayscale}
                    className="scale-75"
                    data-testid="overlay-grayscale-toggle"
                  />
                  <Label className="text-[10px] text-[#94A3B8] cursor-pointer">B&W</Label>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveOverlay}
                  className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                  title="Remove PDF overlay"
                  data-testid="remove-overlay-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
          )}

          <div className="flex items-center gap-1 bg-[#121212] border border-[#1E293B] rounded-md p-1">
            <Button variant="ghost" size="sm" onClick={zoomOut} disabled={zoomLevel <= ZOOM_MIN} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0" data-testid="zoom-out-btn"><ZoomOut className="w-4 h-4" /></Button>
            <span className="text-sm text-[#94A3B8] w-12 text-center">{Math.round(zoomLevel * 100)}%</span>
            <Button variant="ghost" size="sm" onClick={zoomIn} disabled={zoomLevel >= ZOOM_MAX} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0" data-testid="zoom-in-btn"><ZoomIn className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={resetZoom} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0" data-testid="zoom-reset-btn"><RotateCcw className="w-4 h-4" /></Button>
          </div>
          {!isReadOnly && (
            <Button variant="outline" onClick={toggleLock} className={`border-[#1E293B] ${edition.is_locked ? 'text-yellow-400' : 'text-emerald-400'}`} data-testid="lock-toggle-btn">
              {edition.is_locked ? <><Lock className="w-4 h-4 mr-2" /> Locked</> : <><Unlock className="w-4 h-4 mr-2" /> Open</>}
            </Button>
          )}
        </div>
      </div>

      {isReadOnly ? (
        <div className="bg-blue-900/20 border border-blue-800 rounded-md p-3 flex items-center gap-2 text-blue-300 text-sm" data-testid="flatplan-readonly-banner">
          <Lock className="w-4 h-4" /><span>Read-only view — Sales Reps cannot modify Flatplan layouts.</span>
        </div>
      ) : edition.is_locked && (
        <div className="bg-yellow-900/20 border border-yellow-800 rounded-md p-3 flex items-center gap-2 text-yellow-400 text-sm">
          <Lock className="w-4 h-4" /><span>This edition is locked. Unlock it to make changes.</span>
        </div>
      )}

      {/* Main content - sidebar and pages scroll independently */}
      <div className="flex gap-6" style={{ height: 'calc(100vh - 220px)' }}>
        {/* ── Sidebar ── */}
        <div className="w-72 flex-shrink-0 space-y-4 overflow-y-auto">
          {/* Unplaced Bookings */}
          <div className="bg-[#121212] border border-[#1E293B] rounded-md">
            <div className="p-4 border-b border-[#1E293B]">
              <h2 className="text-sm font-medium tracking-wide uppercase text-[#94A3B8]" data-testid="unplaced-header">
                Unplaced Bookings ({unplacedBookings.length})
              </h2>
            </div>
            <div className="p-2 max-h-[40vh] overflow-y-auto">
              {unplacedBookings.length > 0 ? (
                <div className="space-y-2">
                  {unplacedBookings.map((booking) => {
                    const sz = getAdSizePercent(booking, productTypes, pageDimensions);
                    return (
                      <div
                        key={booking.id}
                        draggable={!edition.is_locked && !isReadOnly}
                        onDragStart={(e) => handleDragStart(e, booking)}
                        onDragEnd={handleDragEnd}
                        className={`p-3 bg-[#1A1A1A] border border-[#1E293B] rounded-md cursor-grab hover:border-[#3B82F6]/50 transition-all ${
                          draggedBooking?.id === booking.id ? 'opacity-50 border-[#3B82F6]' : ''
                        } ${(edition.is_locked || isReadOnly) ? 'cursor-not-allowed opacity-60' : ''}`}
                        data-testid={`unplaced-booking-${booking.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-4 h-4 text-[#475569] mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#F8FAFC] text-sm truncate">{booking.advertiser_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-1.5 py-0.5 text-[10px] rounded bg-[#3B82F6]/20 text-[#3B82F6] font-medium">{sz.label}</span>
                              <span className="text-xs text-[#94A3B8]">{booking.product_type}</span>
                            </div>
                            {booking.creative_file && isImageFile(booking.creative_filename) && (
                              <img src={`${API_BASE}${booking.creative_file}`} alt="" className="w-full h-12 object-cover rounded mt-1.5 border border-[#1E293B]" />
                            )}
                            <p className="text-xs text-[#3B82F6] mt-1">{formatCurrency(booking.rate)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center text-[#94A3B8] text-sm">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No unplaced bookings</p>
                  <p className="text-xs mt-1">Confirm Print Edition bookings to place them</p>
                </div>
              )}
            </div>
          </div>

          {/* Placed Bookings */}
          <div className="bg-[#121212] border border-[#1E293B] rounded-md">
            <div className="p-4 border-b border-[#1E293B]">
              <h2 className="text-sm font-medium tracking-wide uppercase text-[#94A3B8]" data-testid="placed-header">
                Placed Bookings ({placedBookings.length})
              </h2>
            </div>
            <div className="p-2 max-h-[40vh] overflow-y-auto">
              {placedBookings.length > 0 ? (
                <div className="space-y-2">
                  {placedBookings.map((booking) => {
                    const sz = getAdSizePercent(booking, productTypes, pageDimensions);
                    return (
                      <div
                        key={booking.id}
                        className="p-3 bg-[#1A1A1A] border border-[#1E293B] rounded-md flex items-start gap-2"
                        data-testid={`placed-sidebar-${booking.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#F8FAFC] text-sm truncate">{booking.advertiser_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/20 text-emerald-400 font-medium">P.{booking.page_number}</span>
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-[#3B82F6]/20 text-[#3B82F6] font-medium">{sz.label}</span>
                            <span className="text-xs text-[#94A3B8]">{booking.product_type}</span>
                          </div>
                          <p className="text-xs text-[#3B82F6] mt-1">{formatCurrency(booking.rate)}</p>
                        </div>
                        {!edition.is_locked && !isReadOnly && (
                          <button
                            onClick={() => handleUnplace(booking.id)}
                            className="mt-0.5 w-6 h-6 rounded-full bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center transition-colors flex-shrink-0"
                            title="Remove from flatplan"
                            data-testid={`unplace-sidebar-${booking.id}`}
                          >
                            <X className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center text-[#94A3B8] text-sm">
                  <p>No placed bookings yet</p>
                  <p className="text-xs mt-1">Drag bookings onto pages to place them</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Flatplan Canvas ── */}
        <div className="flex-1 overflow-auto">
          <div
            className="flatplan-grid bg-[#0F1115] border border-[#1E293B] rounded-md p-6"
          >
            {/* Section labels */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {edition.sections?.map((s, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1 rounded" style={{ backgroundColor: `${s.color}20`, borderLeft: `3px solid ${s.color}` }}>
                  <span className="text-xs font-medium" style={{ color: s.color }}>{s.name}</span>
                  <span className="text-xs text-[#94A3B8]">p.{s.start_page}-{s.end_page}</span>
                </div>
              ))}
            </div>

            {/* Pages grid - columns determined by zoom level */}
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${getGridCols(zoomLevel)}, 1fr)` }}>
              {pages.map((pageNum) => (
                <PageCell
                  key={pageNum}
                  pageNum={pageNum}
                  section={getSectionForPage(pageNum)}
                  pageBookings={getBookingsForPage(pageNum)}
                  fillPercentage={getPageFillPercentage(pageNum)}
                  isDropTarget={dragOverPage?.pageNum === pageNum}
                  isDropValid={dragOverPage?.valid}
                  isTransferTarget={transferTargetPage === pageNum}
                  isDraggingSidebar={!!draggedBooking}
                  isLocked={edition.is_locked || isReadOnly}
                  productTypes={productTypes}
                  pageDimensions={pageDimensions}
                  localOffsets={localOffsets}
                  snapEnabled={snapEnabled}
                  gridCols={GRID_COLS}
                  gridRows={GRID_ROWS}
                  pdfBgImage={overlayImages[pageNum]}
                  overlayOpacity={overlayOpacity}
                  overlayGrayscale={overlayGrayscale}
                  registerRef={(el) => { if (el) pageRefsMap.current[pageNum] = el; else delete pageRefsMap.current[pageNum]; }}
                  onDragOver={(e) => handleDragOver(e, pageNum)}
                  onDrop={(e) => handleDrop(e, pageNum)}
                  onDragLeave={() => setDragOverPage(null)}
                  onAdMouseDown={handleAdMouseDown}
                  onAdDoubleClick={handleAdDoubleClick}
                  onUnplace={handleUnplace}
                  onDeletePage={handleDeletePage}
                />
              ))}

              {/* Add Page card */}
              {!edition.is_locked && !isReadOnly && (
                <button
                  onClick={handleAddPage}
                  className="border-2 border-dashed border-[#1E293B] rounded-md flex items-center justify-center hover:border-[#3B82F6]/50 hover:bg-[#3B82F6]/5 transition-all group"
                  style={{ aspectRatio: pageDimensions ? `${pageDimensions.page_width} / ${pageDimensions.page_height}` : '3 / 4' }}
                  data-testid="add-page-btn"
                >
                  <div className="w-10 h-10 rounded-full border-2 border-[#475569] flex items-center justify-center group-hover:border-[#3B82F6] group-hover:bg-[#3B82F6]/10 transition-colors">
                    <Plus className="w-5 h-5 text-[#475569] group-hover:text-[#3B82F6] transition-colors" />
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-[#121212] border border-[#1E293B] rounded-md p-4">
        <div className="flex items-center gap-6 flex-wrap">
          <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Total Bookings</p><p className="text-xl font-bold text-[#F8FAFC]">{bookings.filter(b => b.category === 'digital_edition').length}</p></div>
          <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Placed</p><p className="text-xl font-bold text-emerald-400">{placedBookings.length}</p></div>
          <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Unplaced</p><p className="text-xl font-bold text-yellow-400">{unplacedBookings.length}</p></div>
          <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Total Sales</p><p className="text-xl font-bold text-[#3B82F6]">{formatCurrency(bookings.filter(b => b.category === 'digital_edition').reduce((s, b) => s + (b.rate || 0), 0))}</p></div>
          {(() => {
            const totalPages = pages.length;
            if (totalPages === 0) {
              return (
                <>
                  <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Ads %</p><p className="text-xl font-bold text-[#F8FAFC]">0%</p></div>
                  <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Paid Ads %</p><p className="text-xl font-bold text-emerald-400">0%</p></div>
                  <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">PSA %</p><p className="text-xl font-bold text-purple-400">0%</p></div>
                </>
              );
            }
            const capacity = totalPages * 100;
            let adsFill = 0, paidFill = 0, psaFill = 0;
            for (const b of placedBookings) {
              if (b.category !== 'digital_edition') continue;
              const fillPct = getAdSizePercent(b, productTypes, pageDimensions).fillPct || 0;
              adsFill += fillPct;
              if ((b.rate || 0) > 0) paidFill += fillPct;
              else psaFill += fillPct;
            }
            const fmt = (v) => `${(v / capacity * 100).toFixed(1)}%`;
            return (
              <>
                <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Ads %</p><p className="text-xl font-bold text-[#F8FAFC]" data-testid="flatplan-ads-pct">{fmt(adsFill)}</p></div>
                <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">Paid Ads %</p><p className="text-xl font-bold text-emerald-400" data-testid="flatplan-paid-ads-pct">{fmt(paidFill)}</p></div>
                <div><p className="text-xs text-[#94A3B8] uppercase tracking-wide">PSA %</p><p className="text-xl font-bold text-purple-400" data-testid="flatplan-psa-pct">{fmt(psaFill)}</p></div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Booking Detail Modal (double-click on ad) */}
      <BookingDetailModal
        isOpen={showBookingModal}
        onClose={() => { setShowBookingModal(false); setEditingBooking(null); }}
        booking={editingBooking}
        onSave={() => { setShowBookingModal(false); setEditingBooking(null); loadData(); }}
      />
    </div>
  );
};

// ─── Page Cell Component ───
const PageCell = React.memo(({
  pageNum, section, pageBookings, fillPercentage,
  isDropTarget, isDropValid, isTransferTarget, isDraggingSidebar, isLocked,
  productTypes, pageDimensions, localOffsets,
  snapEnabled, gridCols, gridRows,
  pdfBgImage, overlayOpacity, overlayGrayscale,
  registerRef,
  onDragOver, onDrop, onDragLeave, onAdMouseDown, onAdDoubleClick, onUnplace, onDeletePage
}) => {
  const pageRef = useRef(null);
  const setRef = useCallback((el) => {
    pageRef.current = el;
    registerRef?.(el);
  }, [registerRef]);

  const borderHighlight = isTransferTarget
    ? 'border-[#8B5CF6] ring-2 ring-[#8B5CF6]/30 bg-[#8B5CF6]/10'
    : isDropTarget
      ? isDropValid ? 'border-[#3B82F6] ring-2 ring-[#3B82F6]/30 bg-[#3B82F6]/5' : 'border-red-500 ring-2 ring-red-500/30 bg-red-500/5'
      : 'border-[#1E293B]';

  return (
    <div
      ref={setRef}
      className={`relative bg-[#121212] border rounded-md overflow-hidden transition-all group/page ${borderHighlight}`}
      style={{
        aspectRatio: pageDimensions ? `${pageDimensions.page_width} / ${pageDimensions.page_height}` : '3 / 4',
        borderTopColor: section?.color || '#1E293B',
        borderTopWidth: '3px',
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      data-testid={`page-${pageNum}`}
    >
      {/* PDF overlay background (bottom layer) */}
      {pdfBgImage && (
        <PdfPageBackground imageSrc={pdfBgImage} opacity={overlayOpacity} grayscale={overlayGrayscale} />
      )}

      {/* Margin guides (dashed) — top/bottom/inside/outside from settings */}
      {pageDimensions && (pageDimensions.margin_top || pageDimensions.margin_bottom || pageDimensions.margin_inside || pageDimensions.margin_outside) ? (() => {
        const w = pageDimensions.page_width || 1;
        const h = pageDimensions.page_height || 1;
        const mt = (pageDimensions.margin_top || 0) / h * 100;
        const mb = (pageDimensions.margin_bottom || 0) / h * 100;
        const mi = (pageDimensions.margin_inside || 0) / w * 100;
        const mo = (pageDimensions.margin_outside || 0) / w * 100;
        // Odd pages = right-hand (inside on LEFT), even pages = left-hand (inside on RIGHT)
        const isRightHand = pageNum % 2 === 1;
        const left = isRightHand ? mi : mo;
        const right = isRightHand ? mo : mi;
        return (
          <div
            aria-hidden="true"
            className="absolute pointer-events-none border border-dashed border-[#3B82F6]/50"
            style={{ top: `${mt}%`, bottom: `${mb}%`, left: `${left}%`, right: `${right}%` }}
            data-testid={`page-${pageNum}-margin-guide`}
          />
        );
      })() : null}

      {/* Grid overlay */}
      {snapEnabled && (
        <div className="absolute inset-0 pointer-events-none z-[6]" data-testid={`grid-overlay-${pageNum}`}>
          {/* Vertical column lines */}
          {Array.from({ length: gridCols - 1 }, (_, i) => (
            <div
              key={`v${i}`}
              className="absolute top-0 bottom-0 border-l border-dashed border-[#475569]/30"
              style={{ left: `${((i + 1) / gridCols) * 100}%` }}
            />
          ))}
          {/* Horizontal row lines */}
          {Array.from({ length: gridRows - 1 }, (_, i) => (
            <div
              key={`h${i}`}
              className="absolute left-0 right-0 border-t border-dashed border-[#475569]/30"
              style={{ top: `${((i + 1) / gridRows) * 100}%` }}
            />
          ))}
        </div>
      )}
      {/* Page number + delete */}
      <div className="absolute top-1 left-1 z-20 flex items-center gap-1">
        <span className="text-[10px] font-mono tracking-widest uppercase text-[#94A3B8] bg-[#0A0A0A]/80 px-1.5 py-0.5 rounded">P.{pageNum}</span>
        {!isLocked && (
          <button
            onClick={(e) => { e.stopPropagation(); onDeletePage(pageNum); }}
            className="w-4 h-4 bg-red-500/80 hover:bg-red-500 rounded flex items-center justify-center opacity-0 group-hover/page:opacity-100 transition-opacity"
            title={`Delete page ${pageNum}`}
            data-testid={`delete-page-${pageNum}`}
          >
            <Trash2 className="w-2.5 h-2.5 text-white" />
          </button>
        )}
      </div>

      {/* Fill indicator */}
      {fillPercentage > 0 && (
        <div className="absolute top-1 right-1 z-20">
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${fillPercentage >= 100 ? 'bg-emerald-900/50 text-emerald-400' : 'bg-blue-900/50 text-blue-400'}`}>
            {Math.round(fillPercentage)}%
          </span>
        </div>
      )}

      {/* Ads — absolutely positioned */}
      {pageBookings.map((booking) => {
        const sz = getAdSizePercent(booking, productTypes, pageDimensions);
        const hasImage = booking.creative_file && isImageFile(booking.creative_filename);
        const offsets = localOffsets[booking.id];
        const xOff = offsets ? offsets.x : (booking.x_offset || 0);
        const yOff = offsets ? offsets.y : (booking.y_offset || 0);

        return (
          <div
            key={booking.id}
            onMouseDown={(e) => pageRef.current && onAdMouseDown(e, booking, pageRef.current)}
            onDoubleClick={(e) => { e.stopPropagation(); onAdDoubleClick(booking); }}
            className={`absolute border border-[#3B82F6] rounded overflow-hidden group select-none ${
              hasImage ? '' : 'bg-[#3B82F6]/20'
            } ${isLocked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}`}
            style={{
              width: `${sz.widthPct}%`,
              height: `${sz.heightPct}%`,
              left: `${xOff}%`,
              top: `${yOff}%`,
              minHeight: '14px',
              zIndex: offsets ? 30 : 10
            }}
            data-testid={`placed-booking-${booking.id}`}
          >
            {hasImage && (
              <img src={`${API_BASE}${booking.creative_file}`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" data-testid={`flatplan-image-${booking.id}`} />
            )}
            <div className="relative z-10 flex flex-col items-center justify-center w-full h-full" style={hasImage ? { backgroundColor: 'rgba(0,0,0,0.35)' } : {}}>
              <p className="text-[9px] font-medium text-[#F8FAFC] text-center truncate w-full leading-tight px-0.5">{booking.advertiser_name}</p>
              <span className="text-[8px] text-[#94A3B8]">{sz.label}</span>
            </div>
            {!isLocked && (
              <button
                onClick={(e) => { e.stopPropagation(); onUnplace(booking.id); }}
                className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20"
                data-testid={`remove-booking-${booking.id}`}
              >
                <span className="text-white text-[10px]">&times;</span>
              </button>
            )}
          </div>
        );
      })}

      {/* Drop zone hint — sidebar DnD */}
      {pageBookings.length === 0 && isDraggingSidebar && !isLocked && (
        <div className="absolute inset-2 border border-dashed border-[#475569] rounded flex items-center justify-center opacity-50">
          <span className="text-[10px] text-[#475569] uppercase">Drop here</span>
        </div>
      )}

      {/* Transfer target hint — inter-page drag */}
      {isTransferTarget && (
        <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
          <span className="px-2 py-1 text-[11px] font-medium bg-[#8B5CF6] text-white rounded shadow-lg">Move here</span>
        </div>
      )}

      {/* Section indicator */}
      {section && (
        <div className="absolute bottom-1 left-1 right-1 z-10">
          <span className="text-[8px] font-medium px-1 py-0.5 rounded" style={{ backgroundColor: `${section.color}30`, color: section.color }}>{section.name}</span>
        </div>
      )}
    </div>
  );
});

export default FlatplanPage;
